import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { UserPlus, Pencil, Trash2, X, Loader2, Users, Building2, ShieldCheck } from 'lucide-react';
import { Badge } from '../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const today = () => new Date().toISOString().slice(0, 10);
const plus12Months = (d) => {
  const dt = new Date(d);
  dt.setFullYear(dt.getFullYear() + 1);
  return dt.toISOString().slice(0, 10);
};

const UsersPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [companySettings, setCompanySettings] = useState([]);
  const [updatingCompany, setUpdatingCompany] = useState(null);
  const [productConfigs, setProductConfigs] = useState([]);

  const emptyForm = {
    first_name: '', last_name: '', email: '', company: '',
    start_date: today(), end_date: plus12Months(today()),
    active: true, role: 'customer', password: '', is_primary: false
  };
  const [formData, setFormData] = useState(emptyForm);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, { headers });
      setUsers(res.data);
    } catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  }, [token]);

  const fetchCompanySettings = useCallback(async () => {
    try {
      const [csRes, pcRes] = await Promise.all([
        axios.get(`${API}/admin/company-settings`, { headers }),
        axios.get(`${API}/product-configs`, { headers }),
      ]);
      setCompanySettings(csRes.data || []);
      setProductConfigs(pcRes.data || []);
    } catch {}
  }, [token]);

  useEffect(() => { fetchUsers(); fetchCompanySettings(); }, [fetchUsers, fetchCompanySettings]);

  // Redirect non-admin users
  useEffect(() => {
    if (user && user.role !== 'admin') navigate('/dashboard');
  }, [user, navigate]);

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (u) => {
    setFormData({
      first_name: u.first_name, last_name: u.last_name, email: u.email,
      company: u.company || '', start_date: u.start_date || '', end_date: u.end_date || '',
      active: u.active, role: u.role, password: '', is_primary: u.is_primary || false
    });
    setEditingId(u.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/users/${id}`, { headers });
      toast.success('User deleted');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleUpdateCompanyLimit = async (company, newLimit) => {
    setUpdatingCompany(company);
    const cs = companySettings.find(c => c.company === company);
    try {
      await axios.put(`${API}/admin/company-settings/${encodeURIComponent(company)}`, {
        max_users: newLimit,
        product_config_id: cs?.product_config_id || '',
      }, { headers });
      toast.success(`License limit for '${company}' set to ${newLimit}`);
      fetchCompanySettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update limit');
    }
    setUpdatingCompany(null);
  };

  const handleUpdateProductConfig = async (company, configId) => {
    setUpdatingCompany(company);
    const cs = companySettings.find(c => c.company === company);
    try {
      await axios.put(`${API}/admin/company-settings/${encodeURIComponent(company)}`, {
        max_users: cs?.max_users || 1,
        product_config_id: configId || '',
      }, { headers });
      const configName = productConfigs.find(pc => pc.id === configId)?.name || 'None';
      toast.success(`Product config for '${company}' set to ${configName}`);
      fetchCompanySettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update config');
    }
    setUpdatingCompany(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.first_name.trim() || !formData.last_name.trim() || !formData.email.trim()) {
      toast.error('First name, last name and email are required');
      return;
    }
    if (!editingId && !formData.password.trim()) {
      toast.error('Initial password is required');
      return;
    }
    setSubmitting(true);
    try {
      if (editingId) {
        const payload = { ...formData };
        if (!payload.password) delete payload.password;
        await axios.put(`${API}/admin/users/${editingId}`, payload, { headers });
        toast.success('User updated');
      } else {
        await axios.post(`${API}/admin/users`, formData, { headers });
        toast.success('User created');
      }
      resetForm();
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save user');
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-violet-600" /></div>;

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="users-heading">Users</h1>
          <p className="text-slate-600 mt-1">Manage user accounts</p>
        </div>
        {!showForm && (
          <Button onClick={() => { setFormData(emptyForm); setEditingId(null); setShowForm(true); }} className="bg-violet-600 hover:bg-violet-700" data-testid="create-user-btn">
            <UserPlus className="w-4 h-4 mr-2" />Create New User
          </Button>
        )}
      </div>

      {/* Company License Settings */}
      {companySettings.length > 0 && (
        <Card className="border-slate-200" data-testid="company-license-settings">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Building2 className="w-5 h-5 text-teal-600" />
              Company License Limits
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Company</th>
                    <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Active Users</th>
                    <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Users</th>
                    <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">License Limit</th>
                    <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Product Configuration</th>
                    <th className="text-left p-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {companySettings.map(cs => (
                    <tr key={cs.company} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`company-row-${cs.company}`}>
                      <td className="p-3 text-sm font-medium text-slate-800">{cs.company}</td>
                      <td className="p-3 text-sm text-slate-700">{cs.active_users}</td>
                      <td className="p-3 text-sm text-slate-700">{cs.total_users}</td>
                      <td className="p-3">
                        <Input
                          type="number"
                          min={1}
                          max={1000}
                          key={`${cs.company}-${cs.max_users}`}
                          defaultValue={cs.max_users}
                          disabled={updatingCompany === cs.company}
                          className="w-24 h-8"
                          data-testid={`license-input-${cs.company}`}
                          onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                          onBlur={(e) => {
                            let v = parseInt(e.target.value, 10);
                            if (isNaN(v)) { e.target.value = cs.max_users; return; }
                            v = Math.max(1, Math.min(1000, v));
                            e.target.value = v;
                            if (v !== cs.max_users) handleUpdateCompanyLimit(cs.company, v);
                          }}
                        />
                      </td>
                      <td className="p-3">
                        <Select
                          value={cs.product_config_id || '__none__'}
                          onValueChange={(v) => handleUpdateProductConfig(cs.company, v === '__none__' ? '' : v)}
                          disabled={updatingCompany === cs.company}
                        >
                          <SelectTrigger className="w-40 h-8" data-testid={`product-config-select-${cs.company}`}>
                            <SelectValue placeholder="None" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">None (All Tools)</SelectItem>
                            {productConfigs.map(pc => (
                              <SelectItem key={pc.id} value={pc.id}>{pc.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-3">
                        {cs.active_users >= cs.max_users ? (
                          <Badge variant="destructive" className="text-xs">At Limit</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs text-emerald-700 border-emerald-200 bg-emerald-50">
                            {cs.max_users - cs.active_users} seat{cs.max_users - cs.active_users !== 1 ? 's' : ''} available
                          </Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Form */}
      {showForm && (
        <Card data-testid="user-form">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle>{editingId ? 'Edit User' : 'Create New User'}</CardTitle>
            <Button variant="ghost" size="icon" onClick={resetForm}><X className="w-4 h-4" /></Button>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>First Name *</Label>
                <Input value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} placeholder="First name" data-testid="user-firstname-input" />
              </div>
              <div>
                <Label>Last Name *</Label>
                <Input value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} placeholder="Last name" data-testid="user-lastname-input" />
              </div>
              <div>
                <Label>Email Address *</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="email@example.com" data-testid="user-email-input" />
              </div>
              <div>
                <Label>Company</Label>
                <Input value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} placeholder="Company name" data-testid="user-company-input" />
              </div>
              <div>
                <Label>Start Date</Label>
                <Input type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} data-testid="user-startdate-input" />
              </div>
              <div>
                <Label>End Date</Label>
                <Input type="date" value={formData.end_date} onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} data-testid="user-enddate-input" />
              </div>
              <div>
                <Label>Role</Label>
                <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="customer">Customer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{editingId ? 'New Password (leave blank to keep)' : 'Set Initial Password *'}</Label>
                <Input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder={editingId ? '(unchanged)' : 'e.g. Password1'} data-testid="user-password-input" />
                {!editingId && (
                  <p className="text-xs text-slate-500 mt-1">User will be forced to change this password on first login.</p>
                )}
                {editingId && formData.password.trim() && (
                  <p className="text-xs text-amber-600 mt-1">User will be forced to change this password on next login.</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="user-active" checked={formData.active} onChange={(e) => setFormData({ ...formData, active: e.target.checked })} className="w-4 h-4 rounded border-slate-300" data-testid="user-active-checkbox" />
                <Label htmlFor="user-active" className="cursor-pointer">Active</Label>
                {formData.role === 'customer' && (
                  <>
                    <input type="checkbox" id="user-primary" checked={formData.is_primary} onChange={(e) => setFormData({ ...formData, is_primary: e.target.checked })} className="w-4 h-4 rounded border-slate-300 ml-4" data-testid="user-primary-checkbox" />
                    <Label htmlFor="user-primary" className="cursor-pointer">Primary <span className="text-xs text-slate-500 font-normal">(manages team product access)</span></Label>
                  </>
                )}
              </div>
              <div className="md:col-span-2 flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                <Button type="submit" className="bg-violet-600 hover:bg-violet-700" disabled={submitting} data-testid="user-submit-btn">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {editingId ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Users Grid */}
      {users.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-500"><Users className="w-12 h-12 mx-auto mb-4 text-slate-300" /><p>No users found</p></CardContent></Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="users-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left p-3 text-sm font-semibold text-slate-600">First Name</th>
                  <th className="text-left p-3 text-sm font-semibold text-slate-600">Last Name</th>
                  <th className="text-left p-3 text-sm font-semibold text-slate-600">Email Address</th>
                  <th className="text-left p-3 text-sm font-semibold text-slate-600">Company</th>
                  <th className="text-left p-3 text-sm font-semibold text-slate-600">Start Date</th>
                  <th className="text-left p-3 text-sm font-semibold text-slate-600">End Date</th>
                  <th className="text-left p-3 text-sm font-semibold text-slate-600">Active</th>
                  <th className="text-left p-3 text-sm font-semibold text-slate-600">Role</th>
                  <th className="text-left p-3 text-sm font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                    <td className="p-3 text-sm text-slate-900">{u.first_name}</td>
                    <td className="p-3 text-sm text-slate-900">{u.last_name}</td>
                    <td className="p-3 text-sm text-slate-700">{u.email}</td>
                    <td className="p-3 text-sm text-slate-600">{u.company || '-'}</td>
                    <td className="p-3 text-sm text-slate-600">{u.start_date || '-'}</td>
                    <td className="p-3 text-sm text-slate-600">{u.end_date || '-'}</td>
                    <td className="p-3 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${u.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {u.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="p-3 text-sm">
                      <div className="flex items-center gap-1.5">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${u.role === 'admin' ? 'bg-violet-100 text-violet-700' : 'bg-blue-100 text-blue-700'}`}>
                          {u.role === 'admin' ? 'Admin' : 'Customer'}
                        </span>
                        {u.is_primary && u.role !== 'admin' && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-violet-600 text-white" data-testid={`primary-tag-${u.id}`}>Primary</span>
                        )}
                      </div>
                    </td>
                    <td className="p-3 text-sm">
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleEdit(u)} data-testid={`edit-user-${u.id}`}>
                          <Pencil className="w-3 h-3 mr-1" />Edit
                        </Button>
                        <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => handleDelete(u.id)} data-testid={`delete-user-${u.id}`}>
                          <Trash2 className="w-3 h-3 mr-1" />Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

export default UsersPage;
